import { KeyRound, MailCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { MdsLogo } from '../components/MdsLogo';

/** The login page's frame, shared by both screens. */
function Frame({ subtitle, children }: { subtitle: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen grid place-items-center bg-canvas p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2 text-center">
          <div className="mds flex justify-center">
            <MdsLogo />
          </div>
          <CardDescription>{subtitle}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

const strong = (p: string) =>
  p.length >= 8 && /[A-Z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);

/**
 * "Mot de passe oublié ?" — ask for the address; an e-mail with a 60-minute single-use link goes
 * to it if it has an account. The answer is the same either way (the page must not reveal which
 * addresses exist).
 */
export function ForgotPassword() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame subtitle={t('auth.reset.forgotTitle')}>
      {sent ? (
        <div className="space-y-4 text-center">
          <MailCheck className="mx-auto h-8 w-8 text-success" />
          <p className="text-sm">{t('auth.reset.sent', { email: email.trim() })}</p>
          <p className="text-xs text-muted-foreground">{t('auth.reset.sentHint')}</p>
          <Link to="/login" className="text-sm underline">
            {t('auth.reset.backToLogin')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('auth.reset.forgotIntro')}</p>
          <div className="space-y-2">
            <Label htmlFor="email">{t('common.email')}</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy || !email.trim()} className="w-full">
            <KeyRound className="h-4 w-4" />
            {t('auth.reset.sendLink')}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            <Link to="/login" className="hover:underline">
              {t('auth.reset.backToLogin')}
            </Link>
          </p>
        </form>
      )}
    </Frame>
  );
}

/** The e-mailed link: choose a new password (same rule as everywhere), then sign in with it. */
export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showApiError, push } = useToast();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    apiClient
      .post<{ data: { valid: boolean; email?: string } }>('/auth/reset-password/check', { token })
      .then(({ data }) => {
        setState(data.data.valid ? 'valid' : 'invalid');
        setEmail(data.data.email ?? '');
      })
      .catch(() => setState('invalid'));
  }, [token]);

  const mismatch = pw2 !== '' && pw !== pw2;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!strong(pw)) {
      push({ tone: 'warning', message: t('auth.force.tooWeak') });
      return;
    }
    if (pw !== pw2) {
      push({ tone: 'warning', message: t('auth.force.mismatch') });
      return;
    }
    setBusy(true);
    try {
      await apiClient.post('/auth/reset-password', { token, password: pw });
      push({ tone: 'success', message: t('auth.reset.done') });
      navigate('/login', { replace: true });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame subtitle={t('auth.reset.resetTitle')}>
      {state === 'checking' ? (
        <p className="text-sm text-muted-foreground text-center">{t('common.loading')}</p>
      ) : state === 'invalid' ? (
        <div className="space-y-4 text-center">
          <p className="text-sm">{t('auth.reset.invalid')}</p>
          <Link to="/forgot-password" className="text-sm underline">
            {t('auth.reset.askAgain')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {email && (
            <p className="text-sm text-muted-foreground">{t('auth.reset.forAccount', { email })}</p>
          )}
          <div className="space-y-2">
            <Label htmlFor="pw">{t('auth.force.new')}</Label>
            <Input
              id="pw"
              type="password"
              required
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('auth.force.hint')}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw2">{t('auth.force.confirm')}</Label>
            <Input
              id="pw2"
              type="password"
              required
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
            />
            {mismatch && <p className="text-xs text-destructive">{t('auth.force.mismatch')}</p>}
          </div>
          <Button type="submit" disabled={busy || !pw || !pw2} className="w-full">
            <KeyRound className="h-4 w-4" />
            {t('auth.reset.save')}
          </Button>
        </form>
      )}
    </Frame>
  );
}
